import { Flex, Button, Select, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import hexToAddress from 'lib/hexToAddress';
import hexToUtf8 from 'lib/hexToUtf8';
import { useI18n } from 'lib/i18n/context';
import Skeleton from 'ui/shared/chakra/Skeleton';
import CopyToClipboard from 'ui/shared/CopyToClipboard';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import HashStringShortenDynamic from 'ui/shared/HashStringShortenDynamic';

import { SHARED_LOGS_KEYS } from './i18n';

interface Props {
  hex: string;
  index: number;
  isLoading?: boolean;
}

type DataType = 'hex' | 'text' | 'address' | 'number';

const VALUE_CONVERTERS: Record<DataType, (hex: string) => string> = {
  hex: (hex) => hex,
  text: hexToUtf8,
  address: hexToAddress,
  number: (hex) => BigInt(hex).toString(),
};
const OPTIONS: Array<DataType> = [ 'hex', 'address', 'text', 'number' ];

const LogTopic = ({ hex, index, isLoading }: Props) => {
  const { t } = useI18n();
  const badgeBg = useColorModeValue('#FBFCFE', '#171717');
  const badgeBorder = useColorModeValue('#E9ECEF', '#222222');
  const badgeText = useColorModeValue('#616161', '#999999');
  const valueColor = useColorModeValue('#000000', '#FFFFFF');
  const [ selectedDataType, setSelectedDataType ] = React.useState<DataType>('hex');

  const handleSelectChange = React.useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDataType(event.target.value as DataType);
  }, []);

  const value = VALUE_CONVERTERS[selectedDataType.toLowerCase() as Lowercase<DataType>](hex);

  const content = (() => {
    switch (selectedDataType) {
      case 'hex':
      case 'number':
      case 'text': {
        return (
          <>
            <Skeleton isLoaded={ !isLoading } overflow="hidden" whiteSpace="nowrap">
              <Flex color={ valueColor } fontFamily="'Oxanium', sans-serif" fontSize="14px" lineHeight="18px" fontWeight={ 500 }>
                <HashStringShortenDynamic hash={ value } fontWeight="500"/>
              </Flex>
            </Skeleton>
            <CopyToClipboard text={ value } isLoading={ isLoading }/>
          </>
        );
      }

      case 'address': {
        return (
          <AddressEntity
            address={{ hash: value, name: '' }}
            isLoading={ isLoading }
          />
        );
      }
    }
  })();

  return (
    <Flex alignItems="center" _notFirst={{ mt: 3 }} overflow="hidden" maxW="100%">
      <Skeleton isLoaded={ !isLoading } mr={ 3 } borderRadius="base">
        <Button
          size="sm"
          fontWeight={ 500 }
          h="24px"
          minW="24px"
          px="8px"
          borderRadius="4px"
          bg={ badgeBg }
          border="1px solid"
          borderColor={ badgeBorder }
          color={ badgeText }
          _hover={{ bg: badgeBg }}
        >
          { index }
        </Button>
      </Skeleton>
      { index !== 0 && (
        <Skeleton isLoaded={ !isLoading } mr={ 3 } flexShrink={ 0 } borderRadius="base">
          <Select
            size="xs"
            borderRadius="base"
            value={ selectedDataType }
            onChange={ handleSelectChange }
            w="auto"
            aria-label={ t(SHARED_LOGS_KEYS.aria.dataType) }
            sx={{
              h: '32px',
              border: '1px solid',
              borderColor: badgeBorder,
              borderRadius: '8px',
              bg: badgeBg,
              color: valueColor,
              fontFamily: '\'Space Grotesk\', sans-serif',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            { OPTIONS.map((option) => <option key={ option } value={ option }>{ t(SHARED_LOGS_KEYS.dataTypes[option]) }</option>) }
          </Select>
        </Skeleton>
      ) }
      { content }
    </Flex>
  );
};

export default React.memo(LogTopic);
